import { mapFileExtensionToIcon, truncateFileName } from '@/shared/helpers/file-helper';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { Card, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import type { Attachment } from '../../models/attachment';
import { attachmentKeys } from '../../queries/attachment-keys';
import { EFileStatus } from './message-input.types';

interface MessageInputFileProps {
  data: Attachment[];
  onRemove: (newData: Attachment[]) => void;
}

const MessageInputFile: React.FC<MessageInputFileProps> = ({ data, onRemove }) => {
  const isPending = useIsMutating({ mutationKey: attachmentKeys.upload });
  const status = isPending > 0 ? EFileStatus.Uploading : EFileStatus.Pending;

  if (data.length === 0) return <div />;
  return (
    <div className='mex:mb-2 mex:flex mex:w-full mex:flex-1 mex:flex-wrap mex:gap-1 mex:overflow-x-auto'>
      {data.map((f) => {
        return (
          <Card
            key={f.id}
            className='mex:group mex:!overflow-visible mex:px-2 mex:py-0 mex:shadow-lg'
            variant='outlined'
          >
            <Stack direction='row' gap={1} alignItems='center' flexWrap='nowrap'>
              {mapFileExtensionToIcon(f.file!.name.split('.').pop() || '')}
              <Typography variant='caption' className='mex:text-nowrap mex:text-[13px]'>
                {truncateFileName(f.file!.name, 20)}
              </Typography>
              {renderStatusIcon(f.url ? EFileStatus.Uploaded : status, () =>
                onRemove(data.filter((item) => item.id !== f.id))
              )}
            </Stack>
          </Card>
        );
      })}
    </div>
  );
};

export default MessageInputFile;

const renderStatusIcon = (status: EFileStatus, onRemove?: () => void) => {
  switch (status) {
    case EFileStatus.Pending:
      return (
        <IconButton size='small' onClick={onRemove} sx={{ fontSize: 18, color: 'error.main' }}>
          <Cancel fontSize='small' />
        </IconButton>
      );
    case EFileStatus.Uploading:
      return (
        <IconButton size='small' sx={{ fontSize: 18, color: 'text.secondary' }} loading>
          <CircularProgress size={16} />
        </IconButton>
      );

    case EFileStatus.Uploaded:
      return (
        <IconButton size='small' sx={{ fontSize: 18, color: 'success.main' }}>
          <CheckCircle fontSize='small' />
        </IconButton>
      );
    default:
      return (
        <IconButton size='small' onClick={onRemove} sx={{ fontSize: 18, color: 'error.main' }}>
          <Cancel fontSize='small' />
        </IconButton>
      );
  }
};
